import { IHeaderGroupAngularComp } from '@ag-grid-community/angular';
import { IHeaderGroupParams } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
  viewChild,
} from '@angular/core';

import { ButtonComponent } from '@shared/components/button/button.component';
import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { WorkflowStep } from '@shared/services/gql.service';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

import { InMonthAdjustmentGridConfigContext } from './ima-grid-config-context';
import { QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

export interface ImaVendorEstimateColumnGroupHeaderParams {
  ctx: InMonthAdjustmentGridConfigContext;
}

interface MenuItem {
  disabled: Signal<boolean>;
  icon: AuxIconName;
  onClick: VoidFunction;
  textColor?: string;
  title: string;
  tooltip: Signal<string>;
}

const editableTMAColumns: (keyof QuarterCloseAdjustmentGridData)[] = [
  'tma_unit',
  'tma_amount',
  'tma_source',
  'historical_adjustment_amount',
];
const functionNotSupportClosedMonthsMessage = 'Functionality is not supported for closed months.';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex items-center justify-center w-full' },
  imports: [ButtonComponent, TooltipDirective, MenuComponent, IconComponent],
  template: `
    <div
      #headerName
      class="truncate"
      [auxTooltip]="
        headerName.offsetWidth < headerName.scrollWidth ? params()?.displayName || '' : ''
      "
    >
      {{ params()?.displayName }}
    </div>
    @if (isSelectedMonthOpen()) {
      <aux-button
        variant="tertiary"
        [disabled]="pencilDisabled()"
        classList="p-[4px] ml-[10px]"
        [auxTooltip]="pencilTooltip()"
        (clickEmit)="pencilClickFn()"
        icon="Pencil"
      />
    }

    <aux-menu
      #vendorEstimateMenu
      class="ml-[10px]"
      className="p-[4px]"
      [showMenuForExternal]="true"
      optionsClass="border rounded-none"
    >
      @for (item of menuItems(); track $index) {
        <button
          type="button"
          role="menuitem"
          class="flex w-full max-w-[370px] items-center p-[8px] text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
          [class.opacity-70]="item.disabled()"
          [class.cursor-not-allowed]="item.disabled()"
          [auxTooltip]="item.disabled() ? item.tooltip() : ''"
          (click)="onItemClick($event, item)"
        >
          <div class="flex space-x-4">
            <aux-icon [name]="item.icon" class="flex text-aux-blue" [class]="item.textColor" />
            <div class="text-left">
              <p class="text-base text-aux-blue" [class]="item.textColor">{{ item.title }}</p>
            </div>
          </div>
        </button>
      }
    </aux-menu>
  `,
})
export class ImaVendorEstimateColumnGroupHeaderComponent implements IHeaderGroupAngularComp {
  workflowQuery = inject(WorkflowQuery);

  permissions = injectPermissions({
    editVendorEstimate: 'PERMISSION_EDIT_VENDOR_ESTIMATE',
  });

  params = signal<(IHeaderGroupParams & ImaVendorEstimateColumnGroupHeaderParams) | undefined>(
    undefined
  );

  ctx = computed(() => this.params()?.ctx);
  deleteVendorEstimateTooltip = computed(() => {
    const { isSelectedMonthOpenOrFuture, selectedMonthHasVendorEstimate, selectedVendors } =
      this.ctx()!.qcaComponent;
    const singleVendorSelected = selectedVendors.length === 1;

    if (!this.permissions().editVendorEstimate) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (!isSelectedMonthOpenOrFuture()) {
      return functionNotSupportClosedMonthsMessage;
    }

    if (this.isVendorEstimatesLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.VENDOR_ESTIMATES_LOCKED;
    }

    if (this.isWorkflowLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE;
    }

    if (!singleVendorSelected) {
      return 'Select a single Vendor Contract to delete the vendor estimate.';
    }

    if (!selectedMonthHasVendorEstimate()) {
      return 'No Vendor Estimate to delete.';
    }

    return '';
  });
  isSelectedMonthOpen = computed(() => this.ctx()!.qcaComponent.isSelectedMonthOpen());

  isVendorEstimatesLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_VENDOR_ESTIMATES
  );
  isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
  );

  isLockedForSelectedMonth = computed(
    () => (this.isWorkflowLocked() || this.isVendorEstimatesLocked()) && this.isSelectedMonthOpen()
  );
  menuItems = computed<MenuItem[]>(() => {
    if (!this.ctx()?.qcaComponent) {
      return [];
    }

    const {
      doesSelectedMonthHasVendorEstimateSupportingDoc,
      isSelectedMonthOpenOrFuture,
      onDownloadVendorEstimates,
      onUploadVendorEstimate,
      removeBudgetVendorEstimate,
      selectedMonthHasVendorEstimate,
      selectedVendors,
    } = this.ctx()!.qcaComponent;

    const singleVendorSelected = selectedVendors.length === 1;

    return [
      {
        disabled: computed(() => !isSelectedMonthOpenOrFuture()),
        icon: 'Send',
        onClick: () => void onUploadVendorEstimate(),
        title: 'Send Vendor Estimate Support',
        tooltip: computed(() => {
          if (!isSelectedMonthOpenOrFuture()) {
            return functionNotSupportClosedMonthsMessage;
          }

          return '';
        }),
      },
      {
        disabled: computed(
          () => !singleVendorSelected || !doesSelectedMonthHasVendorEstimateSupportingDoc()
        ),
        icon: 'Download',
        onClick: () => void onDownloadVendorEstimates(),
        title: 'Download Vendor Estimate',
        tooltip: computed(() => {
          if (!singleVendorSelected) {
            return 'Select a single Vendor Contract to download supporting documents.';
          }

          if (!doesSelectedMonthHasVendorEstimateSupportingDoc()) {
            return 'No documents to download.';
          }

          return '';
        }),
      },
      {
        disabled: computed(
          () =>
            !this.permissions().editVendorEstimate ||
            !isSelectedMonthOpenOrFuture() ||
            this.isLockedForSelectedMonth() ||
            !singleVendorSelected ||
            !selectedMonthHasVendorEstimate()
        ),
        icon: 'Trash',
        onClick: () => void removeBudgetVendorEstimate(),
        textColor: 'text-aux-red-dark',
        title: 'Delete Vendor Estimate',
        tooltip: this.deleteVendorEstimateTooltip,
      },
    ];
  });
  pencilDisabled = computed(() => {
    return (
      !this.permissions().editVendorEstimate ||
      this.isWorkflowLocked() ||
      this.isVendorEstimatesLocked() ||
      (this.ctx()!.possibleEditColumns().veCanBeEdited && this.ctx()!.allEditableColumnsHidden())
    );
  });
  pencilTooltip = computed(() => {
    if (!this.permissions().editVendorEstimate) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (this.isVendorEstimatesLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.VENDOR_ESTIMATES_LOCKED;
    }

    if (this.isWorkflowLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE;
    }

    const { veCanBeEdited } = this.ctx()!.possibleEditColumns();

    if (veCanBeEdited && this.ctx()!.allEditableColumnsHidden()) {
      return MessagesConstants.NO_EDITABLE_COLUMNS_SELECTED_MESSAGE;
    }

    return '';
  });

  menu = viewChild<MenuComponent>('vendorEstimateMenu');

  agInit(params: IHeaderGroupParams & ImaVendorEstimateColumnGroupHeaderParams): void {
    this.params.set(params);
  }

  onItemClick(event: MouseEvent, item: MenuItem) {
    if (item.disabled()) {
      event.stopPropagation();

      return;
    }

    item.onClick();
    this.menu()?.close();
  }

  pencilClickFn = () => {
    const fn = this.ctx()!.qcaComponent.onEditMode;

    return fn(editableTMAColumns);
  };
}
